// src/components/principal/YourPhotosGrid.jsx

import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import timeAgo from "../../utils/timeAgo";

const PAGE_SIZE = 20;

const YourPhotosGrid = ({ onError }) => {
  const [photos, setPhotos] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [story, setStory] = useState("");

  const observerRef = useRef(null);
  const lastRef = (node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (node && hasMore && !loading) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) setPage((p) => p + 1);
        },
        { threshold: 0.5 }
      );
      observerRef.current.observe(node);
    }
  };

  const load = async (p) => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get("/photos/view-photos", {
        params: { my_photos: true, offset: p * PAGE_SIZE, limit: PAGE_SIZE },
      });
      setPhotos((prev) => (p === 0 ? data : [...prev, ...data]));
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      onError?.("Failed to load your photos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const beginEdit = (photo) => {
    setEditingId(photo.id);
    setStory(photo.story || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setStory("");
  };

  const save = async (photoId) => {
    if (!story.trim()) return onError?.("Story cannot be empty.");
    try {
      // ✅ FIXED: hit the FastAPI endpoint under /photos/...
      const { data } = await api.post(`/photos/${photoId}/update-story`, {
        new_story: story,
      });

      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, story } : p))
      );
      setEditingId(null);
      setStory("");
      // optional: toast with data.message
    } catch (e) {
      onError?.(e.response?.data?.detail || "Failed to update story.");
    }
  };

  if (!photos.length && loading)
    return <p className="loading-indicator">Loading...</p>;
  if (!photos.length) return <p>No approved photos uploaded yet.</p>;

  return (
    <div className="photos-content">
      <div className="photos-grid">
        {photos.map((photo, idx) => (
          <div
            key={photo.id}
            className="photo-card"
            ref={idx === photos.length - 1 ? lastRef : null}
          >
            <img
              src={`https://ghrianghraffy.com/${photo.thumbnail_path}`}
              alt={photo.story || "Photo"}
              className="photo-thumbnail"
              loading="lazy"
            />
            {editingId === photo.id ? (
              <div className="edit-story-form">
                <textarea
                  value={story}
                  onChange={(e) => setStory(e.target.value)}
                  placeholder="Edit story and hashtags"
                  className="form-textarea"
                  maxLength={255}
                />
                <button
                  onClick={() => save(photo.id)}
                  className="save-story-button"
                >
                  Save
                </button>
                <button onClick={cancelEdit} className="cancel-story-button">
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <p>{photo.story || "No story provided"}</p>
                <p>Uploaded {photo.created_at ? timeAgo(photo.created_at) : "N/A"}</p>
                <button
                  onClick={() => beginEdit(photo)}
                  className="edit-story-button"
                >
                  Edit Story
                </button>
              </>
            )}
          </div>
        ))}
      </div>
      {loading && <p className="loading-indicator">Loading more photos...</p>}
      {!hasMore && photos.length > 0 && (
        <p className="no-more-photos">No more photos to load.</p>
      )}
    </div>
  );
};

export default YourPhotosGrid;
